import React from "react";

/**
 * AIHostTTSPlayer
 * Mount this ONCE near the root of your app.
 *
 * It exposes:
 *   window.aiSpeak(text: string, opts?: { voice?: string, speed?: number }): Promise<void>
 *   window.aiStop(): void
 *   window.aiPending(): boolean   (a line is playing or queued)
 *
 * Defaults to the Web Speech API.
 * If you set REACT_APP_TTS_CLOUD_URL to your Firebase Function endpoint,
 * it will try cloud TTS and play via a hidden <audio>.
 *
 * Examples:
 *   window.aiSpeak("Welcome to the quiz!", { voice: "alloy", speed: 1.0 });
 *   window.aiStop();
 */

const CLOUD_ENDPOINT = process.env.REACT_APP_TTS_CLOUD_URL || ""; // e.g. https://<region>-<project>.cloudfunctions.net/synthesizeSpeech
const DEFAULT_VOICE = "alloy";
const DEFAULT_SPEED = 1.0;

export default function AIHostTTSPlayer({
  // Web Speech fallback (only used if Cloud TTS fails)
  defaultVoice = DEFAULT_VOICE,
  defaultSpeed = DEFAULT_SPEED,

  // Google Cloud TTS defaults (you can override via props where you mount this)
  cloudVoiceName = "en-GB-Neural2-B",
  cloudLanguageCode = "en-GB",
  cloudSpeakingRate = 1.10,
  cloudPitch = 2.0,

  // IMPORTANT: set this to your real CF URL or keep "/api/tts" if you have the rewrite
  ttsUrl = "/ttsSpeak",   // was "/api/tts"

  style
}) {
  const audioRef = React.useRef(null);
  const webSpeechUtterRef = React.useRef(null);

  // Queue so lines don't overlap or get lost
  const qRef = React.useRef([]);
  const playingRef = React.useRef(false);
  // Prevent immediate duplicate lines (saves cost)
  const lastEnqueueRef = React.useRef({ text: "", at: 0 });
  // Bumped by stopAll so a line still being fetched when speech is cancelled never plays.
  const stopGenRef = React.useRef(0);

  // Helper function to read volume from localStorage (0.0 to 1.0)
  const getTTSVolume = React.useCallback(() => {
    try {
      const stored = localStorage.getItem("opt_tts_volume");
      if (stored !== null) {
        const volume = parseFloat(stored);
        // Clamp to valid range
        return Math.max(0, Math.min(1, isNaN(volume) ? 1.0 : volume));
      }
    } catch {}
    return 1.0; // Default to 100%
  }, []);

  // Apply volume to audio element
  const applyVolume = React.useCallback(() => {
    try {
      const a = audioRef.current;
      if (a) {
        const volume = getTTSVolume();
        a.volume = volume;
      }
    } catch {}
  }, [getTTSVolume]);

  // ---------- Web Speech (fallback) ----------
  const getWebVoices = React.useCallback(() => {
    try { return window.speechSynthesis?.getVoices?.() || []; } catch { return []; }
  }, []);

  const stopWebSpeech = React.useCallback(() => {
    try {
      if (webSpeechUtterRef.current) {
        webSpeechUtterRef.current.onend = null;
        webSpeechUtterRef.current.onerror = null;
        webSpeechUtterRef.current = null;
      }
      window.speechSynthesis?.cancel?.();
    } catch {}
  }, []);

  const speakWithWebSpeech = React.useCallback(async (text, { voice = defaultVoice, speed = defaultSpeed } = {}) => {
    console.info("[TTS] Using Web Speech fallback");
    stopWebSpeech();
    if (!("speechSynthesis" in window)) throw new Error("Web Speech API not available");

    const utter = new SpeechSynthesisUtterance(String(text));
    const voices = getWebVoices();

    // Prefer exact voice name; otherwise try locale match; else browser default
    let match = voices.find(v => v.name?.toLowerCase() === String(voice || "").toLowerCase());
    if (!match && typeof voice === "string" && voice.includes("-")) {
      const lang = voice.split(",")[0].trim();
      match = voices.find(v => v.lang?.toLowerCase().startsWith(lang.toLowerCase()));
    }
    if (match) utter.voice = match;

    utter.rate = Math.max(0.5, Math.min(1.5, Number(speed) || defaultSpeed));
    webSpeechUtterRef.current = utter;

    await new Promise((resolve, reject) => {
      utter.onend = resolve;
      utter.onerror = (e) => reject(e?.error || new Error("Web Speech TTS error"));
      window.speechSynthesis.speak(utter);
    });
  }, [defaultVoice, defaultSpeed, getWebVoices, stopWebSpeech]);

  // ---------- Google Cloud TTS (primary) ----------
  const stopCloud = React.useCallback(() => {
    try {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.removeAttribute("src");
        audioRef.current.load();
      }
    } catch {}
  }, []);

const speakWithCloud = React.useCallback(async (text) => {
  console.info("[TTS] Requesting Google Cloud TTS", { ttsUrl, cloudVoiceName });
  const gen = stopGenRef.current;
  const r = await fetch(ttsUrl, {
    method: "POST",
    headers: { "Content-Type": "text/plain" }, // simple request → no preflight
    body: JSON.stringify({
      text: String(text),
      voiceName: cloudVoiceName,
      languageCode: cloudLanguageCode,
      speakingRate: cloudSpeakingRate,
      pitch: cloudPitch
    })
  });
  const data = await r.json();
  if (!r.ok) throw new Error(data?.error || "Cloud TTS failed");
  if (gen !== stopGenRef.current) return; // cancelled while fetching

  const a = audioRef.current;
  if (!a) throw new Error("Audio element missing");
  
  // Apply volume before setting source and playing
  const volume = getTTSVolume();
  a.volume = volume;
  
  a.src = "data:audio/mp3;base64," + data.audioContent;
  console.info("[TTS] Playing Google Cloud TTS (MP3)", { volume });

  await new Promise((resolve, reject) => {
    console.info("[TTS] Cloud audio promise started");
    const onEnded = () => {
      console.info("[TTS] Cloud audio ended");
      cleanup();
      resolve();
    };
    const onError = (e) => {
      console.warn("[TTS] Cloud audio error", e);
      cleanup();
      reject(e || new Error("Cloud TTS audio error"));
    };
    const cleanup = () => {
      try {
        a.removeEventListener("ended", onEnded);
        a.removeEventListener("error", onError);
      } catch {}
    };

    try {
      a.addEventListener("ended", onEnded, { once: true });
      a.addEventListener("error", onError, { once: true });
      const p = a.play();
      if (p && typeof p.catch === "function") {
        p.catch(onError);
      }
    } catch (err) {
      cleanup();
      reject(err);
    }
  });
}, [ttsUrl, cloudVoiceName, cloudLanguageCode, cloudSpeakingRate, cloudPitch, getTTSVolume]);


  // ---------- queue drain ----------
  const drain = React.useCallback(async () => {
    if (playingRef.current) return;
    const next = qRef.current.shift();
    if (!next) return;

    const { text, onend } =
      typeof next === "string" ? { text: next, onend: null } : next;

    playingRef.current = true;
    const gen = stopGenRef.current;
    try {
      await speakWithCloud(text);
      if (typeof onend === "function") {
        try { onend(); } catch (e) { console.error("[TTS] onend handler error", e); }
      }
    } catch (err) {
      console.error("[TTS] Cloud TTS failed:", err);
      // No fallback to Web Speech - only Google Cloud TTS is used
    } finally {
      setTimeout(() => {
        // After a stop, stopAll already reset the queue; a newer line may be playing now.
        if (gen !== stopGenRef.current) return;
        playingRef.current = false;
        drain(); // continue if more lines remain
      }, 200);
    }
  }, [speakWithCloud]);

const enqueue = React.useCallback((line, opts = {}) => {
  if (line == null || line === "") return;

  const text = String(line);
  const now  = Date.now();
  const last = lastEnqueueRef.current;

  // If the exact same text was just asked for, drop it (avoid double billing)
  if (last.text === text && (now - last.at) < 1500) {
    return;
  }
  lastEnqueueRef.current = { text, at: now };

  qRef.current.push({ text, onend: opts.onend });
  setTimeout(drain, 0);
}, [drain]);


  const stopAll = React.useCallback(() => {
    stopGenRef.current += 1;
    try { stopCloud(); } catch {}
    try { stopWebSpeech(); } catch {}
    qRef.current = [];
    playingRef.current = false;
  }, [stopCloud, stopWebSpeech]);

  // Apply volume on mount and listen for changes
  React.useEffect(() => {
    // Apply initial volume
    applyVolume();

    // Listen for storage events (when changed in another tab/window)
    const handleStorageChange = (e) => {
      if (e.key === "opt_tts_volume") {
        applyVolume();
      }
    };
    window.addEventListener("storage", handleStorageChange);

    // Poll localStorage periodically for same-tab updates (storage event doesn't fire in same tab)
    const pollInterval = setInterval(() => {
      applyVolume();
    }, 500);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(pollInterval);
    };
  }, [applyVolume]);

  // Expose globals used by the rest of your app
  React.useEffect(() => {
    const debugWrapper = (text, opts) => {
      try {
        const stack = (new Error().stack || "").split("\n").slice(2, 5).map(s => s.trim());
        console.info("[TTS DEBUG] aiSpeak called", {
          text: String(text),
          opts,
          stack,
        });
      } catch {}
      return enqueue(text, opts);
    };

    // True while a line is playing or more are queued (gaps between queued lines included).
    const pending = () => playingRef.current || qRef.current.length > 0;

    window.aiSpeak = debugWrapper;
    window.aiStop  = stopAll;
    window.aiPending = pending;

    return () => {
      if (window.aiSpeak === debugWrapper) delete window.aiSpeak;
      if (window.aiStop  === stopAll)  delete window.aiStop;
      if (window.aiPending === pending) delete window.aiPending;
    };
  }, [enqueue, stopAll]);

  return (
    <audio ref={audioRef} preload="auto" style={style ?? { display: "none" }} />
  );
}

